// PostStatus.tsx — Moderation case surface rendered inside PostCard.
// Pure render: no store subscriptions. PostCard owns usePostStatus and passes CaseRecord | null down.

import styled from 'styled-components';
import { Icon } from '@/icons';
import { MetaRow } from '@/ui';
import { CATEGORY_LABELS } from '@/core/models';
import type { CaseRecord } from '@/core/models';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PostStatusProps {
  status: CaseRecord | null;
  onAppeal: (caseId: string) => void;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

const Root = styled.div<{ $tone: 'review' | 'danger' | 'success' }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ $tone, theme }) => {
    if ($tone === 'review') return `${theme.color.review}1f`;
    if ($tone === 'danger') return `${theme.color.danger}1f`;
    return `${theme.color.success}1f`;
  }};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TitleIcon = styled(Icon)<{ $tone: 'review' | 'danger' | 'success' }>`
  color: ${({ $tone, theme }) => {
    if ($tone === 'review') return theme.color.review;
    if ($tone === 'danger') return theme.color.danger;
    return theme.color.success;
  }};
  flex-shrink: 0;
`;

const Title = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Body = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.5;
`;

const AppealNote = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
  line-height: 1.5;
`;

const AppealButton = styled.button`
  align-self: flex-start;
  margin-top: 2px;
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid transparent;
  background: ${({ theme }) => `${theme.color.review}22`};
  color: ${({ theme }) => theme.color.review};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ theme }) => `${theme.color.review}33`};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export function PostStatus({ status, onAppeal }: PostStatusProps) {
  if (status === null) return null;

  const { status: caseStatus } = status;

  // ── Under review ──────────────────────────────────────────────────────────
  // 'awaiting-head' renders IDENTICALLY on purpose. The published promise is
  // that a post under review stays fully visible and carries the label from the
  // moment the case opens — and which internal step the Council is on is not
  // the author's business. Splitting a second member-facing label out of it
  // would show governance jargon and imply something is being withheld.
  if (caseStatus === 'under-review' || caseStatus === 'awaiting-head') {
    return (
      <Root className="post-status" role="status" $tone="review">
        <TitleRow>
          <TitleIcon name="hourglass" size={16} $tone="review" />
          <Title>
            <MetaRow>
              <span>Under Review</span>
              <span>The Council is reviewing this post. It stays fully visible.</span>
            </MetaRow>
          </Title>
        </TitleRow>
      </Root>
    );
  }

  // ── Appeal under review ───────────────────────────────────────────────────
  if (caseStatus === 'appeal-review') {
    return (
      <Root className="post-status" role="status" $tone="review">
        <TitleRow>
          <TitleIcon name="hourglass" size={16} $tone="review" />
          <Title>
            <MetaRow>
              <span>Appeal under review</span>
              <span>The Council is re-examining this decision.</span>
            </MetaRow>
          </Title>
        </TitleRow>
      </Root>
    );
  }

  // ── Decided or appeal-resolved ────────────────────────────────────────────
  // Both statuses share a decided outcome view; only 'decided' gets an Appeal button.
  const isRemoved = status.decision?.outcome === 'removed';
  const tone = isRemoved ? 'danger' : 'success';
  const iconName = isRemoved ? 'ban' : 'check';
  const title = isRemoved
    ? `Removed by Council under ${CATEGORY_LABELS[status.category]}`
    : 'Reviewed and kept visible';

  return (
    <Root className="post-status" role="status" $tone={tone}>
      <TitleRow>
        <TitleIcon name={iconName} size={16} $tone={tone} />
        <Title>{title}</Title>
      </TitleRow>

      {status.decision?.explanation && (
        <Body>{status.decision.explanation}</Body>
      )}

      {status.appeal && (
        <AppealNote>
          Appeal {status.appeal.outcome}. {status.appeal.explanation}
        </AppealNote>
      )}

      {/* No Appeal against a case that EXPIRED. There is no ruling to appeal:
          nobody decided it, the post stayed up, and asking for a fresh team
          would re-stamp the "Under Review" label the expiry just removed. The
          server refuses it with a 409 either way (moderation/api.py); this
          stops the button being offered in the first place. */}
      {caseStatus === 'decided' && status.decision?.resolution !== 'expired' && (
        <AppealButton
          type="button"
          onClick={() => onAppeal(status.id)}
        >
          Appeal
        </AppealButton>
      )}
    </Root>
  );
}
